import localStore from 'lib/store';
import React, { type ReactElement, PureComponent } from 'react';
import { connect } from 'react-redux';
import { IS_DOCUMENTS_DOMAIN } from 'reactApp/appHelpers/configHelpers';
import { abSafeFakeDoorSelector, isAllDocumentsInMenu } from 'reactApp/appHelpers/featuresHelpers';
import { allFilesExpandNoSave } from 'reactApp/appHelpers/featuresHelpers/features/allFilesExpand';
import { familyFeaturePreviewFree, familyFeaturePreviewPaid } from 'reactApp/appHelpers/featuresHelpers/features/familyFunctions';
import {
    forceVigboLinkedAccount,
    vigboFeaturesPreviewFree,
    vigboFeaturesPreviewPaid,
} from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import type { FeatureVariants, TemplateVariantString } from 'reactApp/appHelpers/featuresHelpers/types';
import { chooseVariant } from 'reactApp/appHelpers/featuresHelpers/utils';
import { getFamilyAlbumsFeature, getFamilyFoldersFeature } from 'reactApp/modules/family/family.selectors';
import { getFeatureAllDocumentsHideSections } from 'reactApp/modules/features/features.selectors';
import { getDomainFoldersFilterActive } from 'reactApp/modules/home/home.selectors';
import { initPromoTariffs } from 'reactApp/modules/promoTariffs/promoTariffs.module';
import { getSelectedItems } from 'reactApp/modules/selections/selections.items.selectors';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { type CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { getVigboLinkState, hasActiveVigboTariff } from 'reactApp/modules/vigbo/vigbo.selectors';
import { VigboAccountLinkState } from 'reactApp/modules/vigbo/vigbo.types';
import { isWelcomePromoShow } from 'reactApp/modules/welcome/welcome.selectors';
import type { RootState } from 'reactApp/store';
import { ETreeNodeMode, ETreeRootIds, MAX_TREE_LEVEL_LM } from 'reactApp/ui/TreeComponent/TreeComponent.constants';
import { treeRootConfigLM } from 'reactApp/ui/TreeComponent/TreeComponent.data';
import {
    getAllDocumentsTreeConfig,
    getCurrentTreeState,
    getFoldersTree,
    loadFolder,
} from 'reactApp/ui/TreeComponent/TreeComponent.helpers';
import { treeRootConfigAllDocumentsHandler } from 'reactApp/ui/TreeComponent/TreeComponentDocuments.data';
import { TreeNode } from 'reactApp/ui/TreeComponent/TreeNode';
import type { ITreeNodeClickData, TreeNodeData } from 'reactApp/ui/TreeComponent/TreeNode.types';
import { sendGa as sendGaFunc } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

const autoOpenStoragesAtRefresh = [EStorageType.attaches, EStorageType.sharedLinks, EStorageType.sharedIncoming];

interface Props {
    onExpand: ({ id, storage, isOpen, selectedStorage }) => void;
    onClick: (data: ITreeNodeClickData) => void;
    handleScrollIntoViewRef?: (RefObject) => void;
    items: TreeNodeData[];
    selectedId: string;
    treeNodeMod?: ETreeNodeMode;
    gaCategory?: string;
    gaSuffix?: string;
    selectedStorage?: EStorageType;
    userEmail?: ReturnType<typeof UserSelectors.getEmail>;

    needPromo?: boolean;
    initPromoTariffs?();

    search?: string;
    maxTreeLevel?: number;
    selectedIds?: string[];
    domainFoldersFilterActive?: boolean;
    isDragging?: boolean;
    itemsToDrop?: (CloudItem | undefined)[];
    isB2BUser?: boolean;
}

interface State {
    expandAttaches: boolean;
    expandHome: boolean;
}

export class TreeComponent extends PureComponent<Props, State> {
    static defaultProps = {
        gaCategory: 'leftnav',
        gaSuffix: '',
    };

    public readonly state = {
        expandAttaches: false,
        expandHome: false,
    };

    public componentDidMount(): void {
        this.sendGa('menu', 'show');

        const { initPromoTariffs, needPromo } = this.props;

        if (needPromo && initPromoTariffs) {
            initPromoTariffs();
        }

        const { items, selectedStorage = EStorageType.home, onExpand } = this.props;

        items.forEach((item) => {
            const forceOpen = this.checkForceItem(item);

            if (forceOpen) {
                if (!this.state.expandAttaches && item.id === '/attaches' && selectedStorage !== EStorageType.attaches) {
                    // trigger attaches folders load
                    this.setState({ expandAttaches: true }, () => {
                        onExpand({ id: item.id, storage: item.storage, selectedStorage, isOpen: true });
                    });
                }

                if (!this.state.expandHome && item.id === '/' && selectedStorage !== EStorageType.home) {
                    // trigger home folders load
                    this.setState({ expandHome: true }, () => {
                        onExpand({ id: item.id, storage: item.storage, selectedStorage, isOpen: true });
                    });
                }
            }
        });
    }

    private sendGa = (action, label = '', sendImmediately = false) => {
        const { gaCategory, gaSuffix } = this.props;

        sendGaFunc(gaCategory, action, label, gaSuffix ? { [gaSuffix]: 1 } : null, sendImmediately);
    };

    private getLocalStoreKey(id): string {
        return `${this.props.userEmail}|leftnav-expand:${id}`;
    }

    private handleOnExpand = ({ id, gaId, storage, isOpen, level, selectedStorage }) => {
        this.sendGa('expand', gaId || storage);
        const { onExpand, userEmail, isB2BUser } = this.props;

        /* CLOUDWEB-18216 Эксперимент: разворачивание пункта "Все файлы" только в рамках сессии */
        const allowSave =
            isB2BUser ||
            !allFilesExpandNoSave ||
            (storage !== EStorageType.home && storage !== EStorageType.albums && storage !== EStorageType.gallery);

        // level == 0 for root nodes
        if (userEmail && !level && allowSave) {
            localStore.set(this.getLocalStoreKey(id), isOpen);
        }

        if (isOpen) {
            onExpand({ id, storage, isOpen, selectedStorage });
        }

        sendPaymentGa({
            eventCategory: ECategoryGa.menu,
            action: isOpen ? 'open' : 'close',
            previous_source: storage,
            root: !level,
        });

        return Promise.resolve();
    };

    private handleOnClick = ({ id, gaId, level, href, storage, isSharedOrMounted, isThisOrParentPublic }: ITreeNodeClickData) => {
        if (id === ETreeRootIds.promocodes) {
            return;
        }

        this.props.onClick({ id, level, href, storage, isSharedOrMounted, isThisOrParentPublic });
        this.sendGa((level ? 'tree-' : 'menu-') + (gaId || storage), 'click', true);
        this.sendGa('click', '', true);
    };

    private checkForceItem = (item) => {
        const { items, selectedStorage = EStorageType.home, userEmail, treeNodeMod, isB2BUser } = this.props;
        let forceOpen = items?.length === 1;

        if (selectedStorage === item.storage && autoOpenStoragesAtRefresh.includes(item.storage)) {
            forceOpen = true;
        }

        /* CLOUDWEB-18216 Эксперимент: разворачивание пункта "Все файлы" только в рамках сессии */
        const allowSave = isB2BUser || !allFilesExpandNoSave;

        if (userEmail && allowSave) {
            const previouslyExpanded = localStore.get(this.getLocalStoreKey(item.id));
            forceOpen = typeof previouslyExpanded === 'undefined' ? forceOpen : previouslyExpanded;

            if (item.id === ETreeRootIds.promocodes && previouslyExpanded === null) {
                localStore.set(this.getLocalStoreKey(item.id), true);
                forceOpen = true;
            }
        }

        /*
            принудительно раскрываем Личные папки для бизового юзера,
            для корректной работы перехода в папки из раздела Общие
        */
        if (
            (isB2BUser && item.id === '/' && selectedStorage !== EStorageType.home) ||
            (treeNodeMod === ETreeNodeMode.selectDlg && isB2BUser)
        ) {
            forceOpen = true;
        }

        return forceOpen;
    };

    public render(): ReactElement | null {
        const {
            items,
            selectedId,
            selectedStorage = EStorageType.home,
            treeNodeMod,
            maxTreeLevel,
            search,
            domainFoldersFilterActive,
            isDragging = false,
            itemsToDrop = [],
        } = this.props;

        return (
            <>
                {items.map((item) => (
                    <TreeNode
                        key={item.id}
                        selectedId={domainFoldersFilterActive ? '/domain' : selectedId}
                        selectedStorage={selectedStorage}
                        data={item}
                        mod={treeNodeMod}
                        onExpand={this.handleOnExpand}
                        onClick={this.handleOnClick}
                        forceOpen={this.checkForceItem(item)}
                        handleScrollIntoViewRef={this.props.handleScrollIntoViewRef}
                        maxTreeLevel={maxTreeLevel}
                        search={search}
                        domainFoldersFilterActive={domainFoldersFilterActive}
                        isParentSharedOrMounted={item.isMountedOrShared}
                        isParentPublic={item.isPublic}
                        isDragging={isDragging}
                        itemsToDrop={itemsToDrop}
                    />
                ))}
            </>
        );
    }
}

const mapStateToProps = (state: RootState) => {
    const currState = getCurrentTreeState(state);
    const hiddenSections = getFeatureAllDocumentsHideSections(state);
    const isAllDocuments = IS_DOCUMENTS_DOMAIN || currState?.storage === EStorageType.alldocuments;

    const hasFamilyFoldersFeature = getFamilyFoldersFeature(state);
    const hasFamilyAlbumsFeature = getFamilyAlbumsFeature(state);
    const isPaid = UserSelectors.isPaidUser(state);
    const isB2BUser = UserSelectors.isB2BUser(state);

    const showToFreeUser = familyFeaturePreviewFree && !isPaid;
    const showToPaidUser = familyFeaturePreviewPaid && isPaid;

    const showFamilyFeature = (hasFamilyFoldersFeature && hasFamilyAlbumsFeature) || showToFreeUser || showToPaidUser;

    /**
     * Данные из billing/data, на основе которых работает селектор, дергаются в MainPage
     */
    const isVigboActiveTariff = hasActiveVigboTariff(state);

    const isVigboLinkedAccount = forceVigboLinkedAccount || getVigboLinkState(state) === VigboAccountLinkState.linked;
    const showVigboFeaturesPreview = (vigboFeaturesPreviewFree && !isPaid) || (vigboFeaturesPreviewPaid && isPaid);

    const tree = getFoldersTree(
        state,
        isAllDocuments
            ? getAllDocumentsTreeConfig(hiddenSections)
            : isAllDocumentsInMenu && !isB2BUser
            ? treeRootConfigAllDocumentsHandler({
                  showFamily: showFamilyFeature,
                  showVigbo: isVigboActiveTariff || isVigboLinkedAccount || showVigboFeaturesPreview,
                  isVigboLinkedAccount,
              })
            : treeRootConfigLM(
                  isB2BUser,
                  chooseVariant(
                      abSafeFakeDoorSelector,
                      { control: false, variant2: true } as FeatureVariants<TemplateVariantString, boolean> & { control: boolean },
                      { skipCondition: { variant1: true } }
                  )
              )
    );

    const userEmail = UserSelectors.getEmail(state);
    const showWelcome = isWelcomePromoShow(state);
    const needPromo = !isB2BUser && !IS_DOCUMENTS_DOMAIN;
    const selectedIds = SelectionsSelectors.getSelectedIdxs(state);
    const domainFoldersFilterActive = getDomainFoldersFilterActive(state);
    const isDragging = SelectionsSelectors.isDragging(state);
    const itemsToDrop = getSelectedItems(state);

    let selectedId = currState?.id || `${EStorageType.home}/`;

    if (showWelcome) {
        selectedId = '';
    } else if (currState?.storage === EStorageType.familyAlbums) {
        selectedId = `${EStorageType.familyFolders}/${EStorageType.familyFolders}`;
    }

    if (isB2BUser) {
        tree.forEach((item) => {
            if (item.id === '/') {
                item.children = item.children?.filter((item) => item.kind !== 'domain-folder' && item.kind !== 'mounted');
            }
        });
    }

    return {
        items: tree,
        selectedId,
        selectedStorage: currState?.storage,
        userEmail,
        needPromo,
        maxTreeLevel: MAX_TREE_LEVEL_LM,
        selectedIds,
        domainFoldersFilterActive,
        itemsToDrop,
        isDragging,
        isB2BUser,
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    onExpand: (data) => (ownProps.onExpand ? ownProps.onExpand : loadFolder)({ ...data, dispatch }),
    initPromoTariffs: () => dispatch(initPromoTariffs()),
});

export const TreeConnected = connect(mapStateToProps, mapDispatchToProps)(TreeComponent);
